import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import WIBClock from "../components/WIBClock";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Users, Megaphone, Wallet, AlertTriangle, ArrowUpRight, ArrowDownRight, CalendarDays, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { pengumumanAPI, kegiatanAPI, kasAPI, iuranAPI, laporanAPI, usersAPI } from "../lib/api";

const formatIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n);

const StatCard = ({ icon: Icon, label, value, hint, color = "emerald" }) => (
  <Card className="card-hover border-slate-100">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
          <div className="text-2xl font-extrabold text-slate-800 mt-1" style={{ fontFamily: "Poppins, sans-serif" }}>{value}</div>
          {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
        </div>
        <div className={`h-11 w-11 rounded-xl grid place-items-center ${color === "amber" ? "bg-amber-100 text-amber-600" : color === "red" ? "bg-red-100 text-red-600" : color === "blue" ? "bg-blue-100 text-blue-600" : "bg-emerald-100 text-emerald-700"}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user, theme } = useApp();
  const [data, setData] = useState({ pengumuman: [], kegiatan: [], kas: { pemasukan: 0, pengeluaran: 0, saldoAkhir: 0 }, iuran: [], laporan: [], users: [] });

  useEffect(() => {
    const fetchAll = () => Promise.all([
      pengumumanAPI.list().catch(() => []),
      kegiatanAPI.list().catch(() => []),
      kasAPI.get().catch(() => ({ pemasukan: 0, pengeluaran: 0, saldoAkhir: 0 })),
      iuranAPI.list().catch(() => []),
      laporanAPI.list().catch(() => []),
      usersAPI.list().catch(() => []),
    ]).then(([pengumuman, kegiatan, kas, iuran, laporan, users]) =>
      setData({ pengumuman, kegiatan, kas, iuran, laporan, users })
    );
    fetchAll();
    // Realtime auto-refresh every 25 seconds
    const iv = setInterval(fetchAll, 25000);
    const onFocus = () => fetchAll();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);

  const totalWarga = data.users.filter((u) => u.role === "warga").length;
  const iuranLunas = data.iuran.filter((i) => i.bulan === "Juli 2025" && i.status === "lunas").length;
  const iuranBelum = data.iuran.filter((i) => i.bulan === "Juli 2025" && i.status === "belum").length;
  const laporanBaru = data.laporan.filter((l) => l.status !== "selesai").length;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl brand-gradient text-white p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 h-64 w-64 rounded-full bg-white/10" />
        <div className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-amber-400/20" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div>
            <div className="text-sm text-emerald-100/90">Assalamualaikum,</div>
            <h1 className="text-2xl md:text-3xl font-extrabold mt-1" style={{ fontFamily: "Poppins, sans-serif" }}>Selamat datang, {user.name}</h1>
            <p className="text-emerald-50/90 max-w-xl text-sm mt-2">{theme.welcomeText}</p>
          </div>
          <WIBClock className="bg-white/95 !border-white/60 min-w-[240px]" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Warga" value={totalWarga} hint="Terdaftar" />
        <StatCard icon={Wallet} label="Iuran Lunas Juli" value={`${iuranLunas} KK`} hint={`Belum bayar: ${iuranBelum} KK`} color="amber" />
        <StatCard icon={TrendingUp} label="Saldo Kas RT" value={formatIDR(data.kas.saldoAkhir || 0)} hint="Per Juli 2025" color="blue" />
        <StatCard icon={AlertTriangle} label="Laporan Aktif" value={laporanBaru} hint="Belum selesai" color="red" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Megaphone className="h-5 w-5 brand-text" />
                <h3 className="font-bold text-slate-800" style={{ fontFamily: "Poppins, sans-serif" }}>Pengumuman Terbaru</h3>
              </div>
              <Link to="/pengumuman" className="text-xs font-semibold brand-text hover:underline">Lihat semua →</Link>
            </div>
            <div className="space-y-3">
              {data.pengumuman.slice(0, 4).map((p) => (
                <div key={p.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-emerald-50/50 transition border border-slate-100">
                  <div className="h-10 w-10 rounded-lg bg-emerald-100 grid place-items-center shrink-0">
                    <Megaphone className="h-5 w-5 text-emerald-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-800 text-sm">{p.title}</div>
                      {p.pinned && <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px]">Penting</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{p.body}</p>
                    <div className="text-[10px] text-slate-400 mt-1">{p.author} • {p.date}</div>
                  </div>
                </div>
              ))}
              {data.pengumuman.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Belum ada pengumuman</div>}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays className="h-5 w-5 brand-text" />
              <h3 className="font-bold text-slate-800" style={{ fontFamily: "Poppins, sans-serif" }}>Kegiatan Mendatang</h3>
            </div>
            <div className="space-y-3">
              {data.kegiatan.slice(0, 4).map((k) => (
                <div key={k.id} className="flex gap-3 p-2 rounded-lg hover:bg-emerald-50/50 transition">
                  <div className="h-12 w-12 rounded-lg brand-gradient text-white grid place-items-center shrink-0 leading-none">
                    <div className="text-center">
                      <div className="text-[9px] uppercase">{["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"][parseInt((k.tanggal || "1970-01-01").split("-")[1], 10) - 1]}</div>
                      <div className="text-lg font-bold">{(k.tanggal || "").split("-")[2]}</div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-800 truncate">{k.nama}</div>
                    <div className="text-xs text-slate-500">{k.jam} • {k.lokasi}</div>
                  </div>
                </div>
              ))}
              {data.kegiatan.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Belum ada kegiatan</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-100">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-bold text-slate-800" style={{ fontFamily: "Poppins, sans-serif" }}>Ringkasan Kas RT</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
              <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold"><ArrowUpRight className="h-4 w-4" /> PEMASUKAN</div>
              <div className="text-xl font-extrabold text-emerald-800 mt-1">{formatIDR(data.kas.pemasukan || 0)}</div>
            </div>
            <div className="p-4 rounded-xl bg-red-50 border border-red-100">
              <div className="flex items-center gap-2 text-red-700 text-xs font-semibold"><ArrowDownRight className="h-4 w-4" /> PENGELUARAN</div>
              <div className="text-xl font-extrabold text-red-800 mt-1">{formatIDR(data.kas.pengeluaran || 0)}</div>
            </div>
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
              <div className="flex items-center gap-2 text-amber-700 text-xs font-semibold"><Wallet className="h-4 w-4" /> SALDO AKHIR</div>
              <div className="text-xl font-extrabold text-amber-800 mt-1">{formatIDR(data.kas.saldoAkhir || 0)}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
